import React from 'react';
import { select } from 'redux-saga/effects';
import { defineMessages, FormattedMessage } from 'react-intl';
import _ from 'lodash';

// eslint-disable-next-line import/no-cycle
import store from '../store';
import * as actions from '../state';
import createBoardService from './services/board/createBoard';
import updateBoardService from './services/board/updateBoard';
import fetchBoardListService from './services/board/fetchBoardList';
import createChartService from './services/chart/createChart';
import deleteChartService from './services/chart/deleteChart';
import updateChartService from './services/chart/updateChart';

const messages = defineMessages({
  CHANGES_SAVED: {
    id: 'CHANGES_SAVED',
    defaultMessage: 'Changes saved',
  },
});

export default function* saveBoard(event) {
  try {
    const user = yield select((state) => actions.readStateUser(state));
    const charts = yield select((state) => actions.readStateCharts(state));
    const appURL = yield select((state) => actions.selectConfig(state, 'appURL'));

    const board = event.payload;
    const { domain, userId } = user;

    let boardservice;
    let saveBoardAgain = false;

    if (!board.id) { // board id is not set for a new board
      boardservice = createBoardService({
        user: userId,
        domain,
        board,
      });
    } else {
      boardservice = updateBoardService({
        user: userId,
        domain,
        board,
      });
    }

    boardservice.then((newBoardId) => {
      const newPath = appURL + (newBoardId || board.id);
      window.history.pushState({ urlPath: newPath }, '', newPath);

      /* update boardlist */
      fetchBoardListService({ domain }).then((boardList) => {
        store.dispatch(actions.setBoardList(boardList));
      });

      // save each chart
      // eslint-disable-next-line array-callback-return, consistent-return
      const allPromsies = charts.map((chart) => {
        if (chart.isDeleted) { // chart is flagged for deletion
          return deleteChartService({
            user: userId,
            domain,
            chart,
          });
        } if (chart.type === 'new') { // chart is incomplete
          // ignore
        } else if (chart.id.toString().includes('new_')) { // chart is newly created, with a placeholder ID
          if (newBoardId) { // push the board id here, in case the board was just made
            chart.dashboard_id = newBoardId; // eslint-disable-line no-param-reassign
          }

          const placeholderId = chart.id;
          chart.properties.placeholder_id = placeholderId; // eslint-disable-line no-param-reassign

          return createChartService({
            user: userId,
            domain,
            chart,
          }).then((newChartId) => {
            // eslint-disable-next-line no-param-reassign
            chart.id = newChartId; // swap out placeholder ID
            board?.layouts?.lg?.forEach((gridItem) => { // search the layout for the the placeholder, swap it
              if (gridItem.i === placeholderId) {
                // eslint-disable-next-line no-param-reassign
                gridItem.i = newChartId.toString(); // I dont know why gridItem.i needs to be a string, but it do
                saveBoardAgain = true; // the board will need to be saved against after the layout is updated with new IDs
              }
            });
          });
        } else {
          return updateChartService({
            user: userId,
            domain,
            chart,
          });
        }
      });

      Promise.all(allPromsies).then(() => { // all network requests have returned
        if (saveBoardAgain) {
          updateBoardService({
            user: userId,
            domain,
            board,
          });
        }

        // eslint-disable-next-line no-undef
        store.dispatch(actions.setBoard(_.cloneDeep(board)));
        // eslint-disable-next-line no-undef
        store.dispatch(actions.originalBoard(_.cloneDeep(board)));

        // eslint-disable-next-line no-undef
        const newCharts = _.cloneDeep(charts.filter((chart) => !chart.isDeleted && chart.type !== 'new'));
        store.dispatch(actions.setCharts(newCharts)); // clear deleted charts from state
        store.dispatch(actions.originalCharts(newCharts));

        store.dispatch(actions.isDirty(false));
        store.dispatch(actions.isLoading(false));

        store.dispatch(actions.showToaster({ // TODO: move this to the network request, or further downstream
          show: true,
          message: <FormattedMessage {...messages.CHANGES_SAVED} />,
          type: 'success',
        }));
      });
    });
  } catch (error) {
    console.error(error);
  }
}
